import React from "react";
import Hls from "hls.js";
import "./VideoPlayer.css"; // Custom CSS (provided earlier)

export class VideoPreview extends React.Component {
  videoRef = React.createRef();
  hls = null;

  audioCtx = null;
  analyser = null;
  dataArray = null;
  source = null; // store the media element source

  reconnectTimer = null;
  animationFrame = null;

  constructor(props) {
    super(props);

    this.state = {
      glow: false,
      metadata: {
        title: "Live Stream",
        status: "Streaming...",
      },
    };
  }

  componentDidMount() {
    this.initStream();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.cleanup();
      this.initStream();
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  // Handler to resume audio context on user interaction
  handleUserInteraction = () => {
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(err => {
        console.log('AudioContext resume failed:', err);
      });
    }
  };

  cleanup = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.hls?.destroy();
    this.hls = null;

    // Remove event listeners
    const video = this.videoRef.current;
    if (video) {
      video.removeEventListener('click', this.handleUserInteraction);
      video.removeEventListener('volumechange', this.handleUserInteraction);
      video.removeEventListener('play', this.handleUserInteraction);
    }

    // Close audio context and clean up
    this.audioCtx?.close();
    this.audioCtx = null;
    this.source = null;
    this.analyser = null;
  };

  initStream = () => {
    const video = this.videoRef.current;
    if (!video) return;

    if (this.hls) {
      this.hls.destroy();
    }

    video.muted = true; // ✅ REQUIRED for autoplay
    video.playsInline = true;

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 30,
        enableWorker: true,
      });

      this.hls = hls;

      hls.attachMedia(video);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(this.props.src);
      });

      // ✅ CRITICAL FIX: wait for manifest
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video
          .play()
          .then(() => {
            this.setupVolumeGlow();
            this.setState({
              metadata: { title: "Live Stream", status: "LIVE" },
            });
          })
          .catch((err) => {
            console.log("Play blocked:", err);
          });
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          this.setState((prev) => ({
            metadata: { ...prev.metadata, status: "Reconnecting..." },
          }));

          this.reconnectTimer = setTimeout(this.initStream, 1500);
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = this.props.src;

      video.addEventListener("loadedmetadata", () => {
        video.play().catch(console.log);
      });
    }
  };

  setupVolumeGlow = () => {
    const video = this.videoRef.current;
    if (!video) return;

    // Create audio context if not already created
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }

    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 128;
    this.analyser = analyser;

    // Create media element source only once per video element
    if (!this.source) {
      this.source = this.audioCtx.createMediaElementSource(video);
      this.source.connect(analyser);
      analyser.connect(this.audioCtx.destination);
    }

    const bufferLength = analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    // Resume audio context if suspended – may require user interaction
    const resumeContext = () => {
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(err => {
          console.log('AudioContext resume failed, waiting for user interaction', err);
        });
      }
    };

    // Try immediate resume (might fail if no user gesture yet)
    resumeContext();

    // Attach user interaction listeners to resume later if needed
    video.addEventListener('click', this.handleUserInteraction);
    video.addEventListener('volumechange', this.handleUserInteraction);
    video.addEventListener('play', this.handleUserInteraction);

    // Start animation loop for glow effect
    const animate = () => {
      this.animationFrame = requestAnimationFrame(animate);

      if (!this.analyser || !this.dataArray) return;

      this.analyser.getByteFrequencyData(this.dataArray);

      const avg =
        this.dataArray.reduce((a, b) => a + b, 0) / this.dataArray.length;

      this.setState({ glow: avg > 40 });
    };

    animate();
  };

  render() {
    const { glow, metadata } = this.state;

    return (
      <div className={`video-preview ${glow ? "video-preview--glow" : ""}`}>
        {/* Inner glass panel */}
        <div className="video-preview__inner">
          {/* Floating LIVE Badge */}
          <div className="video-preview__badge">{metadata.status}</div>

          {/* Title */}
          <div className="video-preview__title">
            <span className="video-preview__title-icon">🎬</span>
            {metadata.title}
          </div>

          {/* Video */}
          <video
            ref={this.videoRef}
            controls
            muted
            playsInline
            autoPlay
            className="video-preview__video"
          />
        </div>
      </div>
    );
  }
}